import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Logo, EASE } from './bits';

function LangToggle({ className = '', idPrefix = 'lang-toggle' }) {
  const { lang, setLang } = useLanguage();
  return (
    <div className={`flex items-center gap-1 font-mono text-xs font-semibold tracking-[0.2em] ${className}`} data-testid={idPrefix}>
      <button
        type="button"
        onClick={() => setLang('en')}
        aria-pressed={lang === 'en'}
        data-testid={`${idPrefix}-en`}
        className={`px-1.5 py-1 transition-colors ${lang === 'en' ? 'text-[#C6A15B]' : 'text-white/50 hover:text-white'}`}
      >
        EN
      </button>
      <span className="text-white/30" aria-hidden="true">|</span>
      <button
        type="button"
        onClick={() => setLang('fr')}
        aria-pressed={lang === 'fr'}
        data-testid={`${idPrefix}-fr`}
        className={`px-1.5 py-1 transition-colors ${lang === 'fr' ? 'text-[#C6A15B]' : 'text-white/50 hover:text-white'}`}
      >
        FR
      </button>
    </div>
  );
}

export default function Header() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const links = [
    { to: '/', label: t.nav.home, id: 'home' },
    { to: '/services', label: t.nav.services, id: 'services' },
    { to: '/about', label: t.nav.about, id: 'about' },
    { to: '/seasonal', label: t.nav.seasonal, id: 'seasonal' },
    { to: '/contact', label: t.nav.contact, id: 'contact' },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-ink/90 shadow-lg shadow-black/20 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 sm:px-12 lg:px-20">
          <Logo />
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.to === '/'}
                data-testid={`nav-${l.id}`}
                className={({ isActive }) =>
                  `group relative font-display text-[13px] font-bold uppercase tracking-[0.16em] transition-colors ${
                    isActive ? 'text-white' : 'text-white/60 hover:text-white'
                  }`
                }
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#C6A15B] transition-all duration-300 group-hover:w-full" aria-hidden="true" />
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-6 lg:flex">
            <LangToggle />
            <Link
              to="/contact"
              data-testid="header-quote-button"
              className="bg-white px-6 py-3 font-display text-xs font-bold uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:bg-[#C6A15B]"
            >
              {t.nav.getQuote}
            </Link>
          </div>
          <div className="flex items-center gap-4 lg:hidden">
            <LangToggle idPrefix="mobile-lang-toggle" />
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label={t.nav.openMenu}
              data-testid="mobile-menu-button"
              className="p-2 text-white"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ink lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <Logo />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t.nav.closeMenu}
                data-testid="mobile-menu-close"
                className="p-2 text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-4" aria-label="Mobile">
              {links.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === '/'}
                    data-testid={`mobile-nav-${l.id}`}
                    className={({ isActive }) =>
                      `block py-2.5 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl ${
                        isActive ? 'text-[#C6A15B]' : 'text-white'
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.5, ease: EASE }}
              className="shrink-0 border-t border-white/10 bg-ink p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
            >
              <Link
                to="/contact"
                data-testid="mobile-menu-quote-button"
                className="flex w-full items-center justify-center bg-[#C6A15B] py-4 font-display text-sm font-bold uppercase tracking-[0.18em] text-ink"
              >
                {t.nav.getQuote}
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
